import { useEffect, useRef } from 'react';

export interface FilterField {
  key:         string;
  label:       string;
  type:        'text' | 'select' | 'date';
  placeholder?: string;
  options?:    { label: string; value: string | number }[];
}

interface Props {
  fields:   FilterField[];
  values:   Record<string, string | number | null>;
  onChange: (key: string, value: string | number | null) => void;
  onReset:  () => void;
}

/**
 * Shared filter bar used across all grid pages.
 * Text inputs are debounced 300 ms.
 */
export default function FilterBar({ fields, values, onChange, onReset }: Props) {
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const handleText = (key: string, val: string) => {
    clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(() => onChange(key, val || null), 300);
  };

  // Cleanup on unmount
  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  return (
    <div className="flex flex-wrap gap-3 items-end mb-4">
      {fields.map((f) => (
        <div key={f.key} className="flex flex-col min-w-[150px]">
          <label className="label">{f.label}</label>
          {f.type === 'date' ? (
            <input
              type="date"
              className="input-field"
              value={values[f.key] ?? ''}
              onChange={(e) => onChange(f.key, e.target.value || null)}
            />
          ) : f.type === 'select' ? (
            <select
              className="input-field"
              value={values[f.key] ?? ''}
              onChange={(e) => onChange(f.key, e.target.value || null)}
            >
              <option value="">All</option>
              {f.options?.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              className="input-field"
              placeholder={f.placeholder ?? `Search ${f.label}…`}
              defaultValue={values[f.key] ?? ''}
              onChange={(e) => handleText(f.key, e.target.value)}
            />
          )}
        </div>
      ))}
      <button onClick={onReset} className="btn-secondary h-[38px] mt-auto">
        Clear
      </button>
    </div>
  );
}
